import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { z } from "zod";

import { Box, Typography } from "@mui/material";
import { DangerButton, PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useStyles } from "./AdminLicensesPage.style";

export const licenseFieldWidth = {
    number: 112,
    userEmail: 280,
    licenseKey: 320,
};

type FieldConfig = {
    name: string;
    label?: string;
    placeholder?: string;
    numeric?: boolean;
    width?: number;
};

type Tone = "danger" | "destructive";

type HeadingProps = {
    title: string;
    description: string;
    icon: string;
    tone?: Tone;
};

type FormProps<T extends object> = {
    title: string;
    buttonLabel: string;
    fields: FieldConfig[];
    schema: z.ZodType<T>;
    initialValues: T;
    tone?: Tone;
    onSubmit: (values: T, form: FormApi<T>) => Promise<void>;
    children?: React.ReactNode;
};

export const ActionHeading = (props: HeadingProps) => {
    const { classes, cx } = useStyles();

    return (
        <Box className={classes.actionHeader}>
            <Box className={cx(classes.actionIcon, props.tone === "destructive" && classes.destructiveIcon)}>
                <i className={props.icon} />
            </Box>
            <Box className={classes.actionTitleRow}>
                <Typography variant="subtitle1" fontWeight="bold">
                    {props.title}
                </Typography>
                <Typography variant="body2" className={classes.actionDescription}>
                    {props.description}
                </Typography>
            </Box>
        </Box>
    );
};

const digitsOnly = (value: unknown) => String(value ?? "").replace(/\D/g, "");

const Field = ({ field, fill }: { field: FieldConfig; fill: boolean }) => {
    const { classes, cx } = useStyles();

    return (
        <Box className={cx(classes.fieldSlot, fill && classes.growField)} style={fill ? undefined : { width: field.width }}>
            <TextField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                fullWidth
                config={field.numeric ? { parse: digitsOnly } : undefined}
                slotProps={{
                    htmlInput: {
                        ...(!field.label && field.placeholder ? { "aria-label": field.placeholder } : {}),
                        ...(field.numeric ? { inputMode: "numeric" as const } : {}),
                    },
                }}
            />
        </Box>
    );
};

export const LicenseActionForm = <T extends object>(props: FormProps<T>) => {
    const { classes } = useStyles();
    const validate = useValidationSchema(props.schema);
    const SubmitButton = props.tone ? DangerButton : PrimaryButton;

    return (
        <Form<T>
            onSubmit={props.onSubmit}
            initialValues={props.initialValues}
            validate={validate}
            render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} noValidate className={classes.form} aria-label={props.title}>
                    <Box className={classes.fieldRow}>
                        {props.fields.map((field, index) => (
                            <Field key={field.name} field={field} fill={index === props.fields.length - 1} />
                        ))}
                    </Box>

                    {props.children}

                    <Box className={classes.submitRow}>
                        <SubmitButton type="submit" disabled={submitting} loading={submitting}>
                            {props.buttonLabel}
                        </SubmitButton>
                    </Box>
                </form>
            )}
        />
    );
};

export const LicenseActionStrip = <T extends object>(props: FormProps<T> & HeadingProps) => (
    <SectionPanel title={<ActionHeading {...props} />}>
        <LicenseActionForm {...props} />
    </SectionPanel>
);
